import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import cn from 'classnames';
import { Badge, Box, Button, IconButton, TextButton, Typography } from 'seagull';
import { ChevronDown, ChevronUp } from 'seagull/icons';
import { ChatTextMessage } from '~/components/ai/AgentDraftingPane/messages/ChatTextMessage';
import { UserMessage } from '~/components/ai/AgentDraftingPane/messages/UserMessage';
import { AiGradientShell } from '~/components/ai/AiGradientShell/AiGradientShell';
import { AiWorkingOnIt } from '~/components/ai/AiWorkingOnIt/AiWorkingOnIt';
import { PromptInputField } from '~/components/ai/PromptInputField/PromptInputField';
import { useStickyAutoScroll } from '~/hooks/useStickyAutoScroll';
import { useLocaleKeys } from '~/i18n';
import styles from './AnalystAgentPane.module.scss';
import { GetAnalystAgentPaneTID } from './AnalystAgentPane.utils';
import { useAnalystConversation } from './hooks/useAnalystConversation';
import { useRaiseIntercomLauncher } from './hooks/useRaiseIntercomLauncher';
import { AnalystChartMessage } from './messages/AnalystChartMessage';
import { AnalystFeedbackBar } from './messages/AnalystFeedbackBar';
import { AnalystResultTableMessage } from './messages/AnalystResultTableMessage';
import { AnalystSummaryMessage } from './messages/AnalystSummaryMessage';
import { shouldShowFeedbackBar, useAnalystChatStore } from './store/analystChatStore';
export const AnalystAgentPane = (param)=>{
    let { isOpen: controlledIsOpen, onOpenChange, pollIntervalMs, feedbackDismissMs, testId = '' } = param;
    const { t } = useLocaleKeys('analystAgent');
    const { AnalystAgentPaneTestIds } = useMemo(()=>GetAnalystAgentPaneTID(testId), [
        testId
    ]);
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = controlledIsOpen != null;
    const isOpen = isControlled ? controlledIsOpen : internalOpen;
    const setOpen = (open)=>{
        if (!isControlled) setInternalOpen(open);
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(open);
    };
    useRaiseIntercomLauncher(isOpen);
    const { send, retry, reset, submitFeedback, markAnswerRated } = useAnalystConversation({
        pollIntervalMs
    });
    const { scrollRef, contentRef, onScroll, scrollToBottom } = useStickyAutoScroll();
    const messages = useAnalystChatStore((s)=>s.messages);
    const isThinking = useAnalystChatStore((s)=>s.isThinking);
    const thinkingLabel = useAnalystChatStore((s)=>s.thinkingLabel);
    const transportError = useAnalystChatStore((s)=>s.transportError);
    const isRecovering = useAnalystChatStore((s)=>s.isRecovering);
    const recoveryFailed = useAnalystChatStore((s)=>s.recoveryFailed);
    const recoveryRetryable = useAnalystChatStore((s)=>s.recoveryRetryable);
    const feedbackEligibleMessageId = useAnalystChatStore((s)=>s.feedbackEligibleMessageId);
    const feedbackEligibleRenderedId = useAnalystChatStore((s)=>s.feedbackEligibleRenderedId);
    const submittedFeedbackIds = useAnalystChatStore((s)=>s.submittedFeedbackIds);
    const showFeedback = shouldShowFeedbackBar(feedbackEligibleMessageId, submittedFeedbackIds);
    const [text, setText] = useState('');
    const onSubmitPrompt = ()=>{
        send(text);
        scrollToBottom();
        setText('');
    };
    const errorCopy = (code)=>{
        switch(code){
            case 'query_timeout':
                return t.errors.queryTimeout();
            case 'service_busy':
                return t.errors.serviceBusy();
            case 'invalid_query':
                return t.errors.invalidQuery();
            case 'no_query_formed':
                return t.errors.noQueryFormed();
            case 'internal_error':
                return t.errors.internalError();
            default:
                return t.errors.internalError();
        }
    };
    const lastMessage = messages[messages.length - 1];
    const lastIsRetryableError = (lastMessage === null || lastMessage === void 0 ? void 0 : lastMessage.role) === 'assistant' && lastMessage.part.type === 'error' && lastMessage.part.retryable;
    const canRetry = transportError != null || lastIsRetryableError === true;
    const onChevronClick = (e)=>{
        e.stopPropagation();
        setOpen(!isOpen);
    };
    const hasMessages = messages.length > 0;
    const showEmptyState = !hasMessages && !isRecovering && !recoveryFailed;
    return /*#__PURE__*/ _jsx(AiGradientShell, {
        className: cn(styles.root, isOpen ? styles.open : styles.collapsed),
        testId: AnalystAgentPaneTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(Box, {
            className: styles.paneInner,
            testId: isOpen ? AnalystAgentPaneTestIds.OpenPane() : AnalystAgentPaneTestIds.CollapsedTab(),
            onClick: isOpen ? undefined : ()=>setOpen(true),
            role: isOpen ? undefined : 'button',
            tabIndex: isOpen ? undefined : 0,
            children: [
                /*#__PURE__*/ _jsxs("header", {
                    className: styles.header,
                    "data-testid": AnalystAgentPaneTestIds.Header(),
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: isOpen ? AnalystAgentPaneTestIds.Title() : AnalystAgentPaneTestIds.CollapsedTabLabel(),
                            weight: "normal",
                            skin: isOpen ? 'standard' : 'secondary',
                            className: styles.title,
                            children: isOpen ? t.header.title() : t.collapsedLabel()
                        }),
                        /*#__PURE__*/ _jsx(Badge, {
                            testId: AnalystAgentPaneTestIds.Beta(),
                            className: styles.beta,
                            skin: "primary",
                            rounded: true,
                            outlined: true,
                            "aria-hidden": !isOpen,
                            children: t.header.beta()
                        }),
                        isOpen && hasMessages && /*#__PURE__*/ _jsx(TextButton, {
                            bi: {
                                name: 'analyst_new_conversation'
                            },
                            className: styles.newConversation,
                            skin: "secondary",
                            underline: "none",
                            weight: "normal",
                            variant: "paragraph2",
                            testId: AnalystAgentPaneTestIds.NewConversation(),
                            onClick: ()=>reset(),
                            children: t.newConversation()
                        }),
                        /*#__PURE__*/ _jsx(IconButton, {
                            bi: {
                                name: isOpen ? 'toggle_analyst_pane' : 'open_analyst_agent'
                            },
                            skin: "transparent",
                            className: styles.chevron,
                            testId: isOpen ? AnalystAgentPaneTestIds.CollapseChevron() : AnalystAgentPaneTestIds.CollapsedTabChevron(),
                            onClick: onChevronClick,
                            "aria-label": isOpen ? 'Collapse' : 'Expand',
                            children: isOpen ? /*#__PURE__*/ _jsx(ChevronUp, {
                                testId: AnalystAgentPaneTestIds.ChevronUp()
                            }) : /*#__PURE__*/ _jsx(ChevronDown, {
                                testId: AnalystAgentPaneTestIds.ChevronDown()
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Box, {
                    className: styles.body,
                    "aria-hidden": !isOpen,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            ref: scrollRef,
                            onScroll: onScroll,
                            className: styles.thread,
                            "data-testid": AnalystAgentPaneTestIds.Thread(),
                            children: /*#__PURE__*/ _jsxs("div", {
                                ref: contentRef,
                                className: styles.threadContent,
                                children: [
                                    showEmptyState && /*#__PURE__*/ _jsxs(Box, {
                                        className: styles.emptyState,
                                        testId: AnalystAgentPaneTestIds.EmptyState(),
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                weight: "bold",
                                                children: t.emptyState.title()
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                skin: "secondary",
                                                children: t.emptyState.subtitle()
                                            })
                                        ]
                                    }),
                                    messages.map((message, index)=>{
                                        var _message_part_suggestions, _message_part_suggestions1;
                                        if (message.role === 'user') {
                                            return /*#__PURE__*/ _jsx(Box, {
                                                className: styles.userMessage,
                                                testId: AnalystAgentPaneTestIds.UserMessage(message.id),
                                                children: /*#__PURE__*/ _jsx(UserMessage, {
                                                    messageId: message.id,
                                                    content: {
                                                        type: 'prompt',
                                                        text: message.text
                                                    }
                                                })
                                            }, message.id);
                                        }
                                        const isLast = index === messages.length - 1;
                                        return /*#__PURE__*/ _jsxs(Box, {
                                            className: styles.assistantMessage,
                                            testId: AnalystAgentPaneTestIds.AssistantMessage(message.id),
                                            children: [
                                                message.part.type === 'nl_summary' && /*#__PURE__*/ _jsx(AnalystSummaryMessage, {
                                                    messageId: message.id,
                                                    text: message.part.text
                                                }),
                                                message.part.type === 'error' && /*#__PURE__*/ _jsx(ChatTextMessage, {
                                                    messageId: message.id,
                                                    content: {
                                                        type: 'chat-text',
                                                        text: errorCopy(message.part.code)
                                                    }
                                                }),
                                                message.part.type === 'result_table' && /*#__PURE__*/ _jsx(AnalystResultTableMessage, {
                                                    messageId: message.id,
                                                    part: message.part
                                                }),
                                                message.part.type === 'chart' && /*#__PURE__*/ _jsx(AnalystChartMessage, {
                                                    messageId: message.id,
                                                    part: message.part
                                                }),
                                                message.part.type === 'clarifying_question' && /*#__PURE__*/ _jsxs(Box, {
                                                    className: styles.clarify,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Typography, {
                                                            testId: AnalystAgentPaneTestIds.ClarifyQuestion(message.id),
                                                            showWhiteSpaces: true,
                                                            applyMarkdownBold: true,
                                                            children: message.part.question
                                                        }),
                                                        isLast && ((_message_part_suggestions = message.part.suggestions) !== null && _message_part_suggestions !== void 0 ? _message_part_suggestions : []).length > 0 && /*#__PURE__*/ _jsx(Box, {
                                                            className: styles.suggestions,
                                                            children: ((_message_part_suggestions1 = message.part.suggestions) !== null && _message_part_suggestions1 !== void 0 ? _message_part_suggestions1 : []).slice(0, 5).map((suggestion, i)=>/*#__PURE__*/ _jsx(Button, {
                                                                    className: styles.suggestion,
                                                                    bi: {
                                                                        name: 'analyst_clarify_suggestion'
                                                                    },
                                                                    skin: "primaryOutline",
                                                                    size: "small",
                                                                    testId: AnalystAgentPaneTestIds.ClarifySuggestion(message.id, i),
                                                                    onClick: ()=>send(suggestion),
                                                                    children: suggestion
                                                                }, suggestion))
                                                        })
                                                    ]
                                                }),
                                                message.id === feedbackEligibleRenderedId && showFeedback && feedbackEligibleMessageId != null && /*#__PURE__*/ _jsx(AnalystFeedbackBar, {
                                                    testId: AnalystAgentPaneTestIds.FeedbackBar(),
                                                    dismissDelayMs: feedbackDismissMs,
                                                    onSubmit: (rating, comment)=>submitFeedback(feedbackEligibleMessageId, rating, comment),
                                                    onDismiss: ()=>markAnswerRated(feedbackEligibleMessageId)
                                                })
                                            ]
                                        }, message.id);
                                    }),
                                    (isThinking || isRecovering) && /*#__PURE__*/ _jsx(AiWorkingOnIt, {
                                        label: isRecovering ? t.recovery.recovering() : thinkingLabel !== null && thinkingLabel !== void 0 ? thinkingLabel : undefined,
                                        testId: AnalystAgentPaneTestIds.ThinkingIndicator()
                                    })
                                ]
                            })
                        }),
                        canRetry && !recoveryFailed && /*#__PURE__*/ _jsxs(Box, {
                            className: styles.errorFooter,
                            children: [
                                transportError != null && /*#__PURE__*/ _jsx(Typography, {
                                    skin: "error",
                                    testId: AnalystAgentPaneTestIds.TransportError(),
                                    children: t.transport.message()
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    bi: {
                                        name: 'analyst_retry'
                                    },
                                    skin: "primaryOutline",
                                    size: "small",
                                    testId: AnalystAgentPaneTestIds.RetryButton(),
                                    onClick: ()=>retry(),
                                    children: t.retry()
                                })
                            ]
                        }),
                        recoveryFailed && /*#__PURE__*/ _jsxs(Box, {
                            className: styles.errorFooter,
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    skin: "error",
                                    testId: AnalystAgentPaneTestIds.RecoveryMessage(),
                                    children: recoveryRetryable ? t.transport.message() : t.recovery.notRetrievable()
                                }),
                                recoveryRetryable && /*#__PURE__*/ _jsx(Button, {
                                    bi: {
                                        name: 'analyst_recovery_retry'
                                    },
                                    skin: "primaryOutline",
                                    size: "small",
                                    testId: AnalystAgentPaneTestIds.RecoveryRetryButton(),
                                    onClick: ()=>retry(),
                                    children: t.retry()
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Box, {
                            className: styles.inputBlock,
                            children: /*#__PURE__*/ _jsx(PromptInputField, {
                                testId: AnalystAgentPaneTestIds.Input(),
                                value: text,
                                onChange: setText,
                                onSubmit: onSubmitPrompt,
                                placeholder: t.input.placeholder(),
                                disabled: isThinking || isRecovering,
                                submitDisabled: isThinking || isRecovering || text.trim().length === 0
                            })
                        })
                    ]
                })
            ]
        })
    });
};
